/* eslint-disable no-unused-vars */
import React, { useState } from "react";
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Plus,
  CheckCircle,
  X,
  Check,
  Trash2,
  Lock,
  History,
} from "lucide-react";

// Mock Data

const CURRENT_USER = {
  name: "Super Admin",
  avatar: "https://picsum.photos/100/100?random=999",
};

const SUB_ADMINS = [
  {
    id: "ADM-1",
    name: "Abebe Kebede",
    initials: "AK",
    avatar: "https://picsum.photos/100/100?random=101",
  },
  {
    id: "ADM-2",
    name: "Sara Mohamed",
    initials: "SM",
    avatar: "https://picsum.photos/100/100?random=102",
  },
  {
    id: "ADM-3",
    name: "John Doe",
    initials: "JD",
    avatar: "https://picsum.photos/100/100?random=103",
  },
  {
    id: "ADM-4",
    name: "Hana Girma",
    initials: "HG",
    avatar: "https://picsum.photos/100/100?random=104",
  },
];

const generateTasks = () => {
  const today = new Date();
  const tasks = [];
  const statuses = ["To Do", "In Progress", "Blocked", "Completed"];
  const priorities = ["Low", "Medium", "High", "Urgent"];

  // Generate for current month + previous + next
  for (let m = -1; m <= 1; m++) {
    const baseMonth = new Date(today.getFullYear(), today.getMonth() + m, 1);
    const daysInMonth = new Date(
      baseMonth.getFullYear(),
      baseMonth.getMonth() + 1,
      0,
    ).getDate();

    for (let i = 0; i < 4; i++) {
      const day = Math.floor(Math.random() * daysInMonth) + 1;
      const date = new Date(baseMonth.getFullYear(), baseMonth.getMonth(), day);
      const dateStr = date.toISOString().split("T")[0];

      // Due date is 2 days after start
      const dDate = new Date(date);
      dDate.setDate(date.getDate() + 2);
      const dueDateStr = dDate.toISOString().split("T")[0];

      tasks.push({
        id: `TSK-${1000 + m * 10 + i}`,
        title:
          i % 2 === 0
            ? `Vetting caregiver – Applicant #${2020 + i}`
            : `Review Monthly Reports`,
        description:
          "Please verify all documents and conduct the phone interview.",
        startDate: dateStr,
        dueDate: dueDateStr,
        priority: priorities[i % 4],
        status: statuses[i % 4],
        assignees: ["ADM-1", i % 2 !== 0 ? "ADM-2" : ""],
        createdBy: "System",
        activityLog: [
          {
            id: `log-create-${i}`,
            type: "creation",
            user: "System",
            text: "Task created",
            timestamp: new Date(date.getTime() - 86400000).toISOString(),
          },
        ],
      });
    }
  }
  return tasks;
};

const Tasks = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [tasks, setTasks] = useState(generateTasks());
  const [draggedTask, setDraggedTask] = useState(null);

  // -- Modals --
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // -- Edit State --
  // We keep a separate draft state for editing to allow "Cancel" to work
  const [draftTask, setDraftTask] = useState(null);

  // -- Status Change State --
  const [pendingStatus, setPendingStatus] = useState(null);
  const [statusComment, setStatusComment] = useState("");
  const [showStatusCommentModal, setShowStatusCommentModal] = useState(false);

  // -- New Task Form State --
  const [newTask, setNewTask] = useState({
    title: "",
    startDate: new Date().toISOString().split("T")[0],
    dueDate: new Date().toISOString().split("T")[0],
    priority: "Medium",
    assignees: [],
    description: "",
  });

  // Calendar Logic - Dynamic calculation based on current state
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    // 0 is the last day of previous month, so month + 1, 0 gives last day of current month
    const days = new Date(year, month + 1, 0).getDate();
    const firstDay = new Date(year, month, 1).getDay();
    return { days, firstDay };
  };

  const { days, firstDay } = getDaysInMonth(currentDate);
  const daysArray = Array.from({ length: days }, (_, i) => i + 1);
  const blanksArray = Array.from({ length: firstDay }, (_, i) => i);

  const formatDateString = (day) => {
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, "0");
    const d = String(day).padStart(2, "0");
    return `${year}-${month}-${d}`;
  };

  const todayStr = new Date().toISOString().split("T")[0];

  // --- Handlers ---

  const handleDragStart = (e, task) => {
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, dateStr) => {
    e.preventDefault();
    if (draggedTask) {
      if (dateStr < todayStr) {
        alert("Cannot move tasks to past dates.");
        setDraggedTask(null);
        return;
      }

      const updatedTask = { ...draggedTask, startDate: dateStr };
      // Log the move?
      const log = {
        id: `log-move-${Date.now()}`,
        type: "update",
        user: CURRENT_USER.name,
        text: `rescheduled to ${dateStr}`,
        timestamp: new Date().toISOString(),
      };
      updatedTask.activityLog = [log, ...updatedTask.activityLog];

      setTasks((prev) =>
        prev.map((t) => (t.id === draggedTask.id ? updatedTask : t)),
      );
      setDraggedTask(null);
    }
  };

  const handleCreate = () => {
    if (!newTask.title || !newTask.startDate) return;

    const task = {
      id: `TSK-${Date.now()}`,
      title: newTask.title,
      description: newTask.description || "",
      startDate: newTask.startDate,
      dueDate: newTask.dueDate || newTask.startDate,
      priority: newTask.priority || "Medium",
      status: "To Do",
      assignees: newTask.assignees || [],
      createdBy: CURRENT_USER.name,
      activityLog: [
        {
          id: `log-${Date.now()}`,
          type: "creation",
          user: CURRENT_USER.name,
          avatar: CURRENT_USER.avatar,
          text: "created this task",
          timestamp: new Date().toISOString(),
        },
      ],
    };

    setTasks([...tasks, task]);
    setShowCreateModal(false);
    setNewTask({
      title: "",
      startDate: todayStr,
      dueDate: todayStr,
      priority: "Medium",
      assignees: [],
      description: "",
    });
  };

  const initiateStatusChange = (newStatus) => {
    setPendingStatus(newStatus);
    setStatusComment("");
    setShowStatusCommentModal(true);
  };

  const confirmStatusChange = () => {
    if (!draftTask || !pendingStatus) return;
    if (!statusComment.trim()) {
      alert("A comment is required for status changes.");
      return;
    }

    const oldStatus = draftTask.status;
    const logItem = {
      id: `log-${Date.now()}`,
      type: "status_change",
      user: CURRENT_USER.name,
      avatar: CURRENT_USER.avatar,
      text: `changed status from ${oldStatus} to ${pendingStatus}`,
      subText: statusComment,
      timestamp: new Date().toISOString(),
    };

    const updatedTask = {
      ...draftTask,
      status: pendingStatus,
      activityLog: [logItem, ...draftTask.activityLog],
    };

    setDraftTask(updatedTask);
    setTasks((prev) =>
      prev.map((t) => (t.id === updatedTask.id ? updatedTask : t)),
    );

    setShowStatusCommentModal(false);
    setPendingStatus(null);
  };

  const saveTaskDetails = () => {
    if (!draftTask) return;

    // Detect changes to log them (Assignments, Priority, etc.)
    const original = tasks.find((t) => t.id === draftTask.id);
    const newLogs = [];

    if (original) {
      if (original.priority !== draftTask.priority) {
        newLogs.push({
          id: `log-p-${Date.now()}`,
          type: "update",
          user: CURRENT_USER.name,
          avatar: CURRENT_USER.avatar,
          text: `changed priority to ${draftTask.priority}`,
          timestamp: new Date().toISOString(),
        });
      }
      const added = draftTask.assignees.filter(
        (x) => !original.assignees.includes(x),
      );
      const removed = original.assignees.filter(
        (x) => !draftTask.assignees.includes(x),
      );

      if (added.length > 0 || removed.length > 0) {
        const addedNames = added
          .map((id) => SUB_ADMINS.find((a) => a.id === id)?.name)
          .join(", ");
        const removedNames = removed
          .map((id) => SUB_ADMINS.find((a) => a.id === id)?.name)
          .join(", ");

        let text = "updated assignees";
        if (addedNames) text += `: added ${addedNames}`;
        if (removedNames)
          text += `${addedNames ? ";" : ":"} removed ${removedNames}`;

        newLogs.push({
          id: `log-a-${Date.now()}`,
          type: "assignment",
          user: CURRENT_USER.name,
          avatar: CURRENT_USER.avatar,
          text: text,
          timestamp: new Date().toISOString(),
        });
      }
    }

    const finalTask = {
      ...draftTask,
      activityLog: [...newLogs, ...draftTask.activityLog],
    };

    setTasks((prev) =>
      prev.map((t) => (t.id === finalTask.id ? finalTask : t)),
    );
    setShowDetailModal(false);
  };

  const handleDelete = () => {
    if (!draftTask) return;
    if (confirm("Are you sure you want to delete this task?")) {
      setTasks((prev) => prev.filter((t) => t.id !== draftTask.id));
      setShowDetailModal(false);
    }
  };

  const isTaskLocked = (task) => {
    return task?.status === "Completed";
  };

  // --- UI Helpers ---

  const getPriorityBadge = (p) => {
    const colors = {
      Low: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
      Medium:
        "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
      High: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
      Urgent: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
    };
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-bold ${colors[p]}`}>
        {p}
      </span>
    );
  };

  const getStatusBadge = (s) => {
    const colors = {
      "To Do":
        "bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700",
      "In Progress":
        "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800",
      Blocked:
        "bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800",
      Completed:
        "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800",
    };
    return (
      <span
        className={`px-2.5 py-1 rounded-full text-xs font-bold border ${colors[s]}`}
      >
        {s}
      </span>
    );
  };

  return (
    <div className="h-[calc(100vh-100px)] flex flex-col animate-in fade-in slide-in-from-bottom-4 duration-500 pb-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-6 px-1">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Task Board
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Manage team workflow and priorities.
          </p>
        </div>

        <div className="flex items-center gap-4">
          {/* Month Nav */}
          <div className="flex items-center bg-white dark:bg-gray-800 p-1 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
            <button
              onClick={() =>
                setCurrentDate(
                  new Date(
                    currentDate.getFullYear(),
                    currentDate.getMonth() - 1,
                    1,
                  ),
                )
              }
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-600 dark:text-gray-300"
            >
              <ChevronLeft size={20} />
            </button>
            <span className="px-4 font-bold text-gray-900 dark:text-white min-w-[140px] text-center">
              {currentDate.toLocaleDateString("en-US", {
                month: "long",
                year: "numeric",
              })}
            </span>
            <button
              onClick={() =>
                setCurrentDate(
                  new Date(
                    currentDate.getFullYear(),
                    currentDate.getMonth() + 1,
                    1,
                  ),
                )
              }
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-600 dark:text-gray-300"
            >
              <ChevronRight size={20} />
            </button>
          </div>

          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-gray-900 dark:bg-white text-white dark:text-gray-900 px-5 py-2.5 rounded-xl font-bold flex items-center gap-2 shadow-lg hover:opacity-90 transition-opacity"
          >
            <Plus size={18} /> Create Task
          </button>
        </div>
      </div>

      {/* Calendar View */}
      <div className="flex-1 bg-white dark:bg-gray-800 rounded-3xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden flex flex-col">
        <div className="grid grid-cols-7 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div
              key={d}
              className="py-3 text-center text-xs font-bold text-gray-400 uppercase tracking-wider"
            >
              {d}
            </div>
          ))}
        </div>

        <div className="flex-1 grid grid-cols-7 auto-rows-fr">
          {blanksArray.map((b) => (
            <div
              key={`blank-${b}`}
              className="bg-gray-50/30 dark:bg-gray-900/20 border-b border-r border-gray-100 dark:border-gray-800"
            />
          ))}

          {daysArray.map((day) => {
            const dateStr = formatDateString(day);
            const dayTasks = tasks.filter((t) => t.startDate === dateStr);
            const isToday = todayStr === dateStr;
            const isPast = dateStr < todayStr;

            return (
              <div
                key={day}
                onDragOver={!isPast ? handleDragOver : undefined}
                onDrop={!isPast ? (e) => handleDrop(e, dateStr) : undefined}
                className={`border-b border-r border-gray-100 dark:border-gray-700 p-2 min-h-[120px] transition-colors relative group ${
                  isToday ? "bg-blue-50/30 dark:bg-blue-900/10" : ""
                } ${isPast ? "bg-gray-50 dark:bg-gray-900/40" : ""}`}
              >
                <div className="flex justify-between items-start mb-2">
                  <span
                    className={`text-sm font-medium w-7 h-7 flex items-center justify-center rounded-full ${
                      isToday
                        ? "bg-blue-600 text-white shadow-md"
                        : "text-gray-500"
                    }`}
                  >
                    {day}
                  </span>
                  {!isPast && (
                    <button
                      onClick={() => {
                        setNewTask((p) => ({
                          ...p,
                          startDate: dateStr,
                          dueDate: dateStr,
                        }));
                        setShowCreateModal(true);
                      }}
                      className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded text-gray-500 transition-opacity"
                    >
                      <Plus size={14} />
                    </button>
                  )}
                </div>

                <div className="space-y-1.5 overflow-y-auto max-h-[100px] custom-scrollbar">
                  {dayTasks.map((task) => (
                    <div
                      key={task.id}
                      draggable={!isPast && task.status !== "Completed"}
                      onDragStart={(e) => handleDragStart(e, task)}
                      onClick={() => {
                        setDraftTask(JSON.parse(JSON.stringify(task)));
                        setShowDetailModal(true);
                      }}
                      className={`p-2 rounded-lg border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-sm cursor-pointer hover:shadow-md transition-all text-xs group/task ${
                        draggedTask?.id === task.id ? "opacity-50" : ""
                      }`}
                    >
                      <div className="flex justify-between items-start mb-1">
                        {getPriorityBadge(task.priority)}
                        {task.status === "Completed" && (
                          <CheckCircle size={12} className="text-emerald-500" />
                        )}
                      </div>
                      <div
                        className={`font-semibold line-clamp-2 mb-1.5 ${
                          task.status === "Completed"
                            ? "line-through text-gray-400"
                            : "text-gray-800 dark:text-gray-200"
                        }`}
                      >
                        {task.title}
                      </div>
                      <div className="flex -space-x-1.5">
                        {task.assignees.map((uid) => {
                          const admin = SUB_ADMINS.find((a) => a.id === uid);
                          if (!admin) return null;
                          return (
                            <img
                              key={uid}
                              src={admin.avatar}
                              className="w-4 h-4 rounded-full border border-white dark:border-gray-800"
                              title={admin.name}
                            />
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* --- Create Task Modal --- */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-lg animate-in fade-in zoom-in-95 duration-200 overflow-hidden">
            <div className="p-6 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                Create New Task
              </h3>
              <button onClick={() => setShowCreateModal(false)}>
                <X size={20} className="text-gray-400" />
              </button>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                  Task Title
                </label>
                <input
                  type="text"
                  value={newTask.title}
                  onChange={(e) =>
                    setNewTask({ ...newTask, title: e.target.value })
                  }
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="E.g. Vetting caregiver Hana K."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                    Start Date
                  </label>
                  <input
                    type="date"
                    min={todayStr}
                    value={newTask.startDate}
                    onChange={(e) =>
                      setNewTask({ ...newTask, startDate: e.target.value })
                    }
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                    Due Date
                  </label>
                  <input
                    type="date"
                    min={newTask.startDate || todayStr}
                    value={newTask.dueDate}
                    onChange={(e) =>
                      setNewTask({ ...newTask, dueDate: e.target.value })
                    }
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                    Priority
                  </label>
                  <select
                    value={newTask.priority}
                    onChange={(e) =>
                      setNewTask({ ...newTask, priority: e.target.value })
                    }
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 appearance-none"
                  >
                    <option>Low</option>
                    <option>Medium</option>
                    <option>High</option>
                    <option>Urgent</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                    Assignees
                  </label>
                  <div className="flex -space-x-2 pt-2">
                    {SUB_ADMINS.map((admin) => (
                      <button
                        key={admin.id}
                        onClick={() => {
                          const current = newTask.assignees || [];
                          const updated = current.includes(admin.id)
                            ? current.filter((id) => id !== admin.id)
                            : [...current, admin.id];
                          setNewTask({ ...newTask, assignees: updated });
                        }}
                        className={`w-8 h-8 rounded-full border-2 transition-transform hover:scale-110 hover:z-10 ${
                          newTask.assignees?.includes(admin.id)
                            ? "border-emerald-500 z-10 ring-2 ring-emerald-500/30"
                            : "border-white dark:border-gray-800 grayscale opacity-60"
                        }`}
                      >
                        <img
                          src={admin.avatar}
                          className="w-full h-full rounded-full"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                  Description
                </label>
                <textarea
                  rows={3}
                  value={newTask.description}
                  onChange={(e) =>
                    setNewTask({ ...newTask, description: e.target.value })
                  }
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                  placeholder="Add context or instructions..."
                />
              </div>
            </div>
            <div className="p-6 pt-0 flex gap-3">
              <button
                onClick={() => setShowCreateModal(false)}
                className="flex-1 py-3 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-xl font-bold"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                className="flex-1 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold transition-colors shadow-lg"
              >
                Create Task
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- Detail/Edit Modal --- */}
      {showDetailModal && draftTask && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div
            className="absolute inset-0 bg-black/20 backdrop-blur-sm"
            onClick={() => setShowDetailModal(false)}
          />
          <div className="w-full max-w-2xl bg-white dark:bg-gray-900 h-full shadow-2xl relative animate-in slide-in-from-right duration-300 flex flex-col">
            {/* Modal Header */}
            <div className="px-8 py-6 border-b border-gray-100 dark:border-gray-800 flex justify-between items-start bg-gray-50/50 dark:bg-gray-900/50">
              <div className="flex-1 pr-8">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-xs font-mono text-gray-400">
                    {draftTask.id}
                  </span>
                  {isTaskLocked(draftTask) && (
                    <span className="flex items-center gap-1 px-2 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-[10px] font-bold text-gray-600 dark:text-gray-300 uppercase">
                      <Lock size={10} /> Locked
                    </span>
                  )}
                </div>
                <input
                  type="text"
                  value={draftTask.title}
                  readOnly={isTaskLocked(draftTask)}
                  onChange={(e) =>
                    setDraftTask({ ...draftTask, title: e.target.value })
                  }
                  className="w-full text-2xl font-bold text-gray-900 dark:text-white bg-transparent border-none outline-none placeholder-gray-400 focus:underline decoration-emerald-500/30 decoration-2 underline-offset-4"
                />
              </div>
              <button
                onClick={() => setShowDetailModal(false)}
                className="p-2 hover:bg-gray-200 dark:hover:bg-gray-800 rounded-full text-gray-400 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <div className="flex-1 overflow-hidden flex flex-col md:flex-row">
              {/* Left Column: Details */}
              <div className="flex-1 overflow-y-auto p-8 space-y-8 custom-scrollbar border-r border-gray-100 dark:border-gray-800">
                {/* Status Control */}
                <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-2xl border border-gray-100 dark:border-gray-700">
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 block">
                    Current Status
                  </label>
                  <div className="flex items-center gap-3">
                    {/* Status Dropdown Logic */}
                    <div className="relative flex-1">
                      <select
                        value={draftTask.status}
                        onChange={(e) => initiateStatusChange(e.target.value)}
                        className="w-full appearance-none pl-4 pr-10 py-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 font-bold text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-emerald-500 shadow-sm cursor-pointer"
                      >
                        <option>To Do</option>
                        <option>In Progress</option>
                        <option>Blocked</option>
                        <option>Completed</option>
                      </select>
                      <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                        {getStatusBadge(draftTask.status)}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 block">
                      Priority
                    </label>
                    <select
                      value={draftTask.priority}
                      disabled={isTaskLocked(draftTask)}
                      onChange={(e) =>
                        setDraftTask({ ...draftTask, priority: e.target.value })
                      }
                      className="w-full p-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm font-medium outline-none disabled:opacity-50"
                    >
                      <option>Low</option>
                      <option>Medium</option>
                      <option>High</option>
                      <option>Urgent</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 block">
                      Assignees
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {SUB_ADMINS.map((admin) => {
                        const isSelected = draftTask.assignees.includes(
                          admin.id,
                        );
                        return (
                          <button
                            key={admin.id}
                            disabled={isTaskLocked(draftTask)}
                            onClick={() => {
                              const current = draftTask.assignees;
                              const updated = current.includes(admin.id)
                                ? current.filter((id) => id !== admin.id)
                                : [...current, admin.id];
                              setDraftTask({
                                ...draftTask,
                                assignees: updated,
                              });
                            }}
                            className={`w-8 h-8 rounded-full border-2 transition-all ${
                              isSelected
                                ? "border-emerald-500 ring-2 ring-emerald-500/20"
                                : "border-transparent opacity-40 hover:opacity-100"
                            }`}
                            title={admin.name}
                          >
                            <img
                              src={admin.avatar}
                              className="w-full h-full rounded-full"
                            />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 block">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={draftTask.startDate}
                      disabled={isTaskLocked(draftTask)}
                      onChange={(e) =>
                        setDraftTask({
                          ...draftTask,
                          startDate: e.target.value,
                        })
                      }
                      className="w-full p-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm font-medium outline-none disabled:opacity-50"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 block">
                      Due Date
                    </label>
                    <input
                      type="date"
                      min={draftTask.startDate}
                      value={draftTask.dueDate}
                      disabled={isTaskLocked(draftTask)}
                      onChange={(e) =>
                        setDraftTask({ ...draftTask, dueDate: e.target.value })
                      }
                      className={`w-full p-2.5 bg-white dark:bg-gray-800 border rounded-lg text-sm font-medium outline-none disabled:opacity-50 ${
                        draftTask.dueDate < todayStr &&
                        draftTask.status !== "Completed"
                          ? "border-red-300 text-red-600"
                          : "border-gray-200 dark:border-gray-700"
                      }`}
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 block">
                    Description
                  </label>
                  <textarea
                    rows={6}
                    value={draftTask.description}
                    readOnly={isTaskLocked(draftTask)}
                    onChange={(e) =>
                      setDraftTask({
                        ...draftTask,
                        description: e.target.value,
                      })
                    }
                    className="w-full p-4 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-xl text-sm leading-relaxed outline-none focus:ring-2 focus:ring-emerald-500 resize-none read-only:bg-gray-100 read-only:text-gray-500"
                    placeholder="Add details, links, or context..."
                  />
                </div>
              </div>

              {/* Right Column: Activity Log */}
              <div className="w-full md:w-80 bg-gray-50 dark:bg-gray-900/30 flex flex-col border-l border-gray-100 dark:border-gray-800">
                <div className="p-4 border-b border-gray-200 dark:border-gray-800 font-bold text-sm text-gray-500 uppercase tracking-wider flex items-center gap-2">
                  <History size={16} /> Activity Log
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-6 custom-scrollbar">
                  {draftTask.activityLog.map((log) => (
                    <div key={log.id} className="flex gap-3 relative">
                      {/* Line connector */}
                      <div className="absolute left-[14px] top-8 bottom-[-24px] w-0.5 bg-gray-200 dark:bg-gray-700 last:hidden"></div>

                      <div className="relative z-10">
                        {log.avatar ? (
                          <img
                            src={log.avatar}
                            className="w-7 h-7 rounded-full border-2 border-white dark:border-gray-800"
                          />
                        ) : (
                          <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center text-[10px] font-bold text-gray-500">
                            SYS
                          </div>
                        )}
                      </div>
                      <div className="flex-1 pt-0.5">
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          <span className="font-bold text-gray-900 dark:text-white">
                            {log.user}
                          </span>{" "}
                          {log.text}
                        </div>
                        {log.subText && (
                          <div className="mt-1.5 p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs italic text-gray-600 dark:text-gray-300 shadow-sm">
                            &quot;{log.subText}&quot;
                          </div>
                        )}
                        <div className="text-[10px] text-gray-400 mt-1 opacity-70">
                          {new Date(log.timestamp).toLocaleString(undefined, {
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Detail Footer */}
            <div className="p-5 border-t border-gray-100 dark:border-gray-800 flex justify-between items-center bg-white dark:bg-gray-900">
              <button
                onClick={handleDelete}
                className="text-red-500 hover:text-red-700 text-sm font-bold flex items-center gap-2 px-4 py-2 hover:bg-red-50 dark:hover:bg-red-900/10 rounded-lg transition-colors"
              >
                <Trash2 size={16} /> Delete Task
              </button>
              <button
                onClick={saveTaskDetails}
                disabled={isTaskLocked(draftTask)}
                className="bg-gray-900 dark:bg-white text-white dark:text-gray-900 px-8 py-3 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Check size={18} /> Save Changes
              </button>
            </div>

            {/* Status Change Comment Modal (Nested) */}
            {showStatusCommentModal && (
              <div className="absolute inset-0 z-50 flex items-center justify-center p-6 bg-white/80 dark:bg-black/80 backdrop-blur-sm">
                <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 w-full max-w-sm animate-in zoom-in-95 duration-200">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                    Update Status
                  </h3>
                  <p className="text-sm text-gray-500 mb-4">
                    You are moving this task to{" "}
                    <span className="font-bold text-emerald-600">
                      {pendingStatus}
                    </span>
                    . Please provide a reason or comment.
                  </p>
                  <textarea
                    autoFocus
                    value={statusComment}
                    onChange={(e) => setStatusComment(e.target.value)}
                    className="w-full p-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm focus:ring-2 focus:ring-emerald-500 outline-none mb-4"
                    placeholder="Reason for status change..."
                    rows={3}
                  />
                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        setShowStatusCommentModal(false);
                        setPendingStatus(null);
                      }}
                      className="flex-1 py-2.5 rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 font-bold text-sm"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={confirmStatusChange}
                      className="flex-1 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm transition-colors"
                    >
                      Confirm
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Tasks;
